/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState } from "react";

// react-router-dom components
import { useNavigate } from "react-router-dom";
import { IconButton, InputAdornment } from "@mui/material";

// @mui material components
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";

// @mui icons
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// Authentication layout components
import BasicLayout from "layouts/authentication/components/BasicLayout";

// Images
import bgImage from "assets/images/loginbackground.jpg";
import mastercardLogo from "assets/images/logos/mastercard.png";
import visaLogo from "assets/images/logos/visa.png";
import pciLogo from "assets/images/pcilogo.png";
import botimLogo from "assets/images/botim.png";
import version from "../../../version";

import { setLoggedIn, setToken, setUser, useMaterialUIController, setLicenseMessage, setRefreshToken, setMerchant } from "../../../context";
import useApi from "../../../components/Utils/api";
import toast from "utils/toast";

function Basic() {
  const [rememberMe, setRememberMe] = useState(false);
  const [controller, dispatch] = useMaterialUIController();
  const api = useApi();
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false); // State for password visibility

  const togglePasswordVisibility = () => setShowPassword((prevShowPassword) => !prevShowPassword);
  const login = (form) => {
    form.preventDefault();
    const data = {
      username: form.target.username.value,
      passwd: form.target.password.value
    }
    api.post('auth/merchant/authenticate', data)
      .then(response => {
        if(response.data.token) {
          let newUser = response.data.user;
          if(newUser.authStatus === "7" || newUser.authStatus === "0"){
            let passwdExpiry = new Date(newUser.newUsrPasswdExp);
            const currentDate = new Date();
            if (passwdExpiry < currentDate) {
              toast.error("Your password has been already expired. Please contact the admin.")
            } else {
              toast.success("Welcome "+newUser.firstName+` , Please reset your password to Proceed`);
              setLoggedIn(dispatch, true);
              setUser(dispatch, response.data.user);
              setMerchant(dispatch, response.data.merchant);
              setToken(dispatch, response.data.token);
              setRefreshToken(dispatch, response.data.refreshToken);
              setLicenseMessage(dispatch, response.data.license);
              navigate("/first-password");
            }
          } else if(newUser.authStatus === "1"){
            toast.success("Welcome "+response.data.user.firstName);
            setLoggedIn(dispatch, true);
            setUser(dispatch, response.data.user);
            setMerchant(dispatch, response.data.merchant);
            setToken(dispatch, response.data.token);
            setRefreshToken(dispatch, response.data.refreshToken);
            setLicenseMessage(dispatch, response.data.license);
            navigate("/dashboard");
          }

        } else {
          toast.error(response.data.error || response.data.message || "Login failed. Please check your credentials.");
        }
      })
      .catch(error => {
        const response = error.response;
        if(response) toast.error(response.data.error || response.data.message || "Login failed. Please check your credentials.");
      });
  }

  const handleSetRememberMe = () => setRememberMe(!rememberMe);

  return (
    <BasicLayout image={bgImage} showBackground hideFooter>
      <MDBox display="flex" alignItems="center" justifyContent="center" mb={4}>
        <MDBox component="img" src="/login_page_logo.png" alt="Logo" sx={{ height: "128px" }} />
      </MDBox>
      <Card sx={{ minHeight: "370px", width: "calc(100% + 20px)", display: "flex", flexDirection: "column" }}>
        <MDBox pt={4} pb={1} px={3} textAlign="left">
          <MDTypography variant="h5" fontWeight="bold" color="dark" sx={{ letterSpacing: "0.5px" }}>
            Welcome
          </MDTypography>
          {/* <MDTypography display="block" variant="button" color="text" fontWeight="regular" mt={1}>
            Enter your credentials to access your account.
          </MDTypography> */}
        </MDBox>
        <MDBox
          pt={1}
          pb={3}
          px={3}
          sx={{ flex: 1, display: "flex", flexDirection: "column" }}
        >
          <MDBox
            component="form"
            role="form"
            onSubmit={(form) => login(form)}
            sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}
          >
            <MDBox>
              <MDBox mb={2}>
                <MDInput type="text" label="Username" name="username" fullWidth required />
              </MDBox>
              <MDBox mb={2}>
                <MDInput
                  type={showPassword ? "text" : "password"}
                  label="Password"
                  name="password"
                  fullWidth
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={togglePasswordVisibility} edge="end">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </MDBox>
              <MDBox display="flex" alignItems="center" justifyContent="space-between" ml={-1}>
                <MDBox display="flex" alignItems="center">
                  <Switch checked={rememberMe} name="remember_me" onChange={handleSetRememberMe} />
                  <MDTypography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    onClick={handleSetRememberMe}
                    sx={{ cursor: "pointer", userSelect: "none", ml: -1, fontSize: "0.75rem" }}
                  >
                    &nbsp;&nbsp;Remember me
                  </MDTypography>
                </MDBox>
                <MDBox component="a" href="/forgot-password">
                  <MDTypography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    sx={{ cursor: "pointer", userSelect: "none", fontSize: "0.75rem" }}
                  >
                    Forgot password?
                  </MDTypography>
                </MDBox>
              </MDBox>
            </MDBox>
            <MDBox mb={1}>
              <MDButton type="submit" variant="gradient" color="info" fullWidth>
                sign in
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
      <MDBox display="flex" alignItems="center" justifyContent="center" gap={3} mt={3}>
        <MDBox component="img" src={mastercardLogo} alt="Mastercard" sx={{ height: "28px" }} />
        <MDBox component="img" src={visaLogo} alt="Visa" sx={{ height: "28px" }} />
        <MDBox component="img" src={pciLogo} alt="PCI DSS Compliant" sx={{ height: "28px" }} />
        <MDBox component="img" src={botimLogo} alt="Botim" sx={{ height: "28px" }} />
      </MDBox>
      <MDBox display="flex" alignItems="center" justifyContent="center" gap={1} mt={1.5} sx={{ whiteSpace: "nowrap" }}>
        <MDTypography variant="caption" color="text" sx={{ fontSize: "0.65rem" }}>
          &copy; {new Date().getFullYear()} YouCloud Technologies. All Rights Reserved.
        </MDTypography>
        <MDTypography variant="caption" color="text" sx={{ fontSize: "0.65rem" }}>
          Version {version}
        </MDTypography>
      </MDBox>
    </BasicLayout>
  );
}

export default Basic;
